import { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Trash2 } from 'lucide-react';

interface SwipeableChecklistItemProps {
  text: string;
  note?: string;
  checked: boolean;
  onToggle: () => void;
  onUpdate: (text: string) => void;
  onDelete: () => void;
}

const SwipeableChecklistItem = ({
  text,
  note,
  checked,
  onToggle,
  onUpdate,
  onDelete,
}: SwipeableChecklistItemProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const constraintsRef = useRef(null);
  const x = useMotionValue(0);
  const deleteOpacity = useTransform(x, [-100, -50], [1, 0]);
  const deleteScale = useTransform(x, [-100, -50], [1, 0.8]);
  const itemOpacity = useTransform(x, [-150, -100], [0.5, 1]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    setIsDragging(false);
    if (info.offset.x < -100) {
      onDelete();
    }
  };

  return (
    <div ref={constraintsRef} className="relative overflow-hidden rounded-lg">
      {/* Delete background */}
      <motion.div
        className="absolute inset-y-0 right-0 flex items-center justify-end px-4 bg-destructive"
        style={{ opacity: deleteOpacity }}
      >
        <motion.div style={{ scale: deleteScale }}>
          <Trash2 className="h-5 w-5 text-destructive-foreground" />
        </motion.div>
      </motion.div>

      {/* Swipeable item */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -150, right: 0 }}
        dragElastic={0.1}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        style={{ x, opacity: itemOpacity }}
        className={`relative flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 border transition-colors cursor-grab active:cursor-grabbing ${
          checked
            ? 'bg-primary/5 border-primary/20'
            : 'bg-card border-border'
        }`}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          className="mt-0.5 pointer-events-auto h-4 w-4 sm:h-5 sm:w-5"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="flex-1 min-w-0">
          <Input
            value={text}
            onChange={(e) => onUpdate(e.target.value)}
            className={`border-0 bg-transparent p-0 h-auto focus-visible:ring-0 text-sm sm:text-base ${
              checked ? 'line-through text-muted-foreground' : ''
            }`}
            placeholder="Enter item..."
            onPointerDown={(e) => {
              if (isDragging) e.preventDefault();
            }}
          />
          {note && (
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">{note}</p>
          )}
        </div>
        
        {/* Swipe hint on mobile */}
        <div className="text-[10px] text-muted-foreground/50 sm:hidden select-none">
          ←
        </div>
      </motion.div>
    </div>
  );
};

export default SwipeableChecklistItem;
